import { useEffect, useState } from 'react';
import { Card, Button, Tag, Empty, Spin, Alert, Typography, Flex, Result } from 'antd';
import {
  CheckCircleOutlined,
  WarningOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
  HeartOutlined,
  PlayCircleOutlined,
  LinkOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import callAPI from '../../comps/fetch/main';
import Sider from '../../comps/sider/main';

const { Title, Text } = Typography;

function getTimeAgo(dateStr) {
  if (!dateStr) return '';
  const now = new Date();
  const then = new Date(dateStr);
  const diffMs = now - then;
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
}

function getSeverityIcon(severity) {
  switch (severity) {
    case 'critical':
      return <CloseCircleOutlined style={{ fontSize: 24, color: '#ff4d4f' }} />;
    case 'warning':
      return <WarningOutlined style={{ fontSize: 24, color: '#faad14' }} />;
    default:
      return <InfoCircleOutlined style={{ fontSize: 24, color: '#1677ff' }} />;
  }
}

function getSeverityColor(severity) {
  switch (severity) {
    case 'critical': return 'red';
    case 'warning': return 'orange';
    default: return 'blue';
  }
}

function FixButton({ fixAction }) {
  if (!fixAction) return null;

  const handleClick = () => {
    if (fixAction.type === 'video' && fixAction.url) {
      window.open(fixAction.url, '_blank');
    } else if (fixAction.type === 'reconnect') {
      // IG-direct accounts reconnect via Instagram login — the plain
      // /ig-connect page is the Facebook-page flow (same routing rule as
      // list-ig-posts / the sider reconnect modal).
      window.location.href = localStorage.getItem('ig_api') === 'true'
        ? '/ig-connect?show_ig_connect=true'
        : '/ig-connect';
    } else if (fixAction.url) {
      window.location.href = fixAction.url;
    }
  };

  const icon = fixAction.type === 'video' ? <PlayCircleOutlined /> :
    fixAction.type === 'reconnect' ? <LinkOutlined /> :
    <LinkOutlined />;

  if (fixAction.type === 'info') {
    return (
      <Tag color="default" style={{ padding: '4px 12px', fontSize: 13 }}>
        {fixAction.label}
      </Tag>
    );
  }

  return (
    <Button type="primary" size="small" icon={icon} onClick={handleClick}>
      {fixAction.label}
    </Button>
  );
}

function IssueCard({ issue }) {
  return (
    <Card
      style={{ marginBottom: 12, borderLeft: `4px solid ${getSeverityColor(issue.severity) === 'red' ? '#ff4d4f' : getSeverityColor(issue.severity) === 'orange' ? '#faad14' : '#1677ff'}` }}
      bodyStyle={{ padding: '16px 20px' }}
    >
      <Flex align="flex-start" gap={16}>
        <div style={{ paddingTop: 2 }}>
          {getSeverityIcon(issue.severity)}
        </div>
        <div style={{ flex: 1 }}>
          <Flex justify="space-between" align="center" wrap="wrap" gap={8}>
            <div>
              <Text strong style={{ fontSize: 15 }}>{issue.title}</Text>
              <Tag color={getSeverityColor(issue.severity)} style={{ marginLeft: 8 }}>
                {issue.severity}
              </Tag>
            </div>
            <FixButton fixAction={issue.fix_action} />
          </Flex>
          <Text type="secondary" style={{ display: 'block', marginTop: 4, fontSize: 13 }}>
            {issue.description}
          </Text>
          <Flex gap={16} style={{ marginTop: 8 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {issue.error_count} error{issue.error_count !== 1 ? 's' : ''}
            </Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              First: {getTimeAgo(issue.first_error_at)}
            </Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Last: {getTimeAgo(issue.last_error_at)}
            </Text>
          </Flex>
        </div>
      </Flex>
    </Card>
  );
}

function Health() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const fetchHealth = () => {
    callAPI({
      url: 'get-health-status',
      method: 'GET',
      loadingState: setLoading,
      dataState: (res) => {
        if (res?.data) {
          setData(res.data);
        }
      },
    });
  };

  useEffect(() => {
    fetchHealth();
  }, []);

  const renderContent = () => {
    if (loading && !data) {
      return (
        <Flex justify="center" align="center" style={{ minHeight: 300 }}>
          <Spin size="large" />
        </Flex>
      );
    }

    if (!data) {
      return <Empty description="Unable to load health status" />;
    }

    if (data.healthy) {
      return (
        <Result
          icon={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
          title="All Good!"
          subTitle="Your account is healthy. No issues detected."
        />
      );
    }

    return (
      <div>
        <Alert
          type="error"
          showIcon
          icon={<WarningOutlined />}
          message={`${data.issues.length} issue${data.issues.length !== 1 ? 's' : ''} found — ${data.total_errors_24h} total errors`}
          description="Fix the issues below to restore your automations."
          style={{ marginBottom: 20 }}
        />
        {data.issues.map((issue, idx) => (
          <IssueCard key={issue.category || idx} issue={issue} />
        ))}
      </div>
    );
  };

  return (
    <div className="page-layout">
      <Sider />
      <div className="page-content">
        <div style={{ maxWidth: 700, margin: '0 auto', padding: '24px 16px' }}>
          <Flex justify="space-between" align="center" style={{ marginBottom: 20 }}>
            <Flex align="center" gap={10}>
              <HeartOutlined style={{ fontSize: 22 }} />
              <Title level={4} style={{ margin: 0 }}>Account Health</Title>
            </Flex>
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchHealth}
              loading={loading}
              size="small"
            >
              Refresh
            </Button>
          </Flex>

          {data && !loading && data.healthy && (
            <Alert
              type="success"
              showIcon
              icon={<CheckCircleOutlined />}
              message="All systems operational"
              style={{ marginBottom: 20 }}
            />
          )}

          {renderContent()}
        </div>
      </div>
    </div>
  );
}

export default Health;
